import React, { useRef, useState, useMemo, useEffect } from 'react';
import { FiUploadCloud, FiX, FiChevronUp, FiChevronDown, FiLink } from 'react-icons/fi';
import Papa from 'papaparse';
import { toast } from 'react-toastify';
import { getAuthContext } from '../../utils/jwt';
import ColumnMapping, { ColumnMappingRef } from './ColumnMapping';
import PreviewSection from './PreviewSection';
import { BASE_URL } from '../../utils/constants';

// Type-cast Feather icons for React compatibility
const FiChevronUpComponent = FiChevronUp as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiChevronDownComponent = FiChevronDown as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiUploadCloudComponent = FiUploadCloud as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiXComponent = FiX as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiLinkComponent = FiLink as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

// Props for the ImportAudienceModal component
interface ImportAudienceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onFieldConfigUpdate?: (newFields: string[]) => void;
}

// Interface for preview data
interface PreviewData {
  headers: string[];
  rows: any[];
  fileName: string;
}

// Interface for import status
interface ImportStatus {
  importId: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  processed: number;
  succeeded: number;
  rejected: number;
  progress: number;
  rejectsUrl: string | null;
  errorMessage: string | null;
}

// Interface for rejected row
interface RejectedRow {
  rowNumber: number;
  reason: string;
}

// Default mapping JSON with correct API values
const defaultMappingJson = {
  "Email": "email",
  "FirstName": "firstName",
  "LastName": "lastName",
  "Phone": "phone",
  "DOB": "dob",
  "DedupKey": "dedupKey",
  "Gender": "gender",
  "Status": "status",
  "Tags": "tags[]",
  "Segments": "segments[]",
  "AddressLine1": "address.line1",
  "City": "address.city",
  "Country": "address.country"
};

// Component for importing audience data from CSV files
const ImportAudienceModal: React.FC<ImportAudienceModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onFieldConfigUpdate,
}) => {
  // Reference for column mapping component
  const columnMappingRef = useRef<ColumnMappingRef>(null);
  // Reference for hidden file input
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // State for uploaded files, preview data, and import steps
  const [files, setFiles] = useState<File[]>([]);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [currentStep, setCurrentStep] = useState<
    'upload' | 'preview' | 'mapping' | 'status'
  >('upload');
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  // State for column mappings and custom field names
  const [mappings, setMappings] = useState<{ [key: string]: string }>({});
  const [customFieldNames, setCustomFieldNames] = useState<{
    [key: string]: string;
  }>({});
  const [isImporting, setIsImporting] = useState(false);
  // State for import mode - default to 'create-only'
  const [importMode, setImportMode] = useState<'create-only' | 'update-only'>('create-only');

  // State for import status tracking
  const [importStatus, setImportStatus] = useState<ImportStatus | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [showRejectedModal, setShowRejectedModal] = useState(false);
  const [rejectedRows, setRejectedRows] = useState<RejectedRow[]>([]);
  const [isLoadingRejected, setIsLoadingRejected] = useState(false);

  // Memoized expected audience labels for preview
  const expectedAudienceLabels = useMemo(
    () => [
      'email',
      'firstName',
      'lastName',
      'phone',
      'dob',
      'dedupKey',
      'gender',
      'status',
      'tags[]',
      'segments[]',
      'address.line1',
      'address.city',
      'address.country',
    ],
    []
  );

  // Memoized possible target fields for mapping
  const possibleTargets = useMemo(
    () => expectedAudienceLabels,
    [expectedAudienceLabels]
  );

  // Checks if all file headers match expected labels
  const allHeadersMatch = useMemo(() => {
    if (!previewData || !previewData.headers) return false;

    const fileHeaders = previewData.headers.map((header) =>
      header.toLowerCase().trim()
    );
    const expectedLabels = expectedAudienceLabels.map((label) =>
      label.toLowerCase().trim()
    );

    // Check if all file headers are in the expected labels
    return fileHeaders.every((header) => expectedLabels.includes(header));
  }, [previewData, expectedAudienceLabels]);

  // Determines button text based on current step
  const getButtonText = () => {
    if (isImporting) return 'Importing...';
    if (currentStep === 'upload') return 'Next';
    if (currentStep === 'preview') return allHeadersMatch ? 'Import' : 'Next';
    if (currentStep === 'mapping') return 'Import';
    if (currentStep === 'status') return 'Close';
    return 'Next';
  };

  // Poll for import status
  useEffect(() => {
    let pollInterval: NodeJS.Timeout | null = null;

    if (isPolling && importStatus && importStatus.status !== 'COMPLETED' && importStatus.status !== 'FAILED') {
      pollInterval = setInterval(async () => {
        await fetchImportStatus(importStatus.importId);
      }, 2000); // Poll every 2 seconds
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [isPolling, importStatus]);

  if (!isOpen) return null;

  // Handles file selection and triggers preview generation
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const newFiles = Array.from(e.target.files);

    // Only accept CSV files
    const csvFiles = newFiles.filter(file => {
      const ext = file.name.split('.').pop()?.toLowerCase();
      return ext === 'csv';
    });

    if (csvFiles.length === 0) {
      toast.error('Please upload only CSV files');
      return;
    }

    setFiles(csvFiles);

    if (csvFiles.length > 0) {
      await generatePreview(csvFiles[0]);
      setCurrentStep('preview');
    }
  };

  // Triggers file input click for re-upload
  const handleReupload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  // Generates preview data from uploaded file
  const generatePreview = async (file: File): Promise<void> => {
    return new Promise((resolve) => {
      const ext = file.name.split('.').pop()?.toLowerCase();

      if (ext === 'csv') {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            const rows = results.data as any[];
            const headers = results.meta.fields || [];
            setPreviewData({
              headers,
              rows: rows.slice(0, 10),
              fileName: file.name,
            });
            prefillMappings(headers);
            resolve();
          },
        });
      } else {
        toast.error('Please upload a CSV file');
        resolve();
      }
    });
  };

  // Prefills mappings for headers that match expected labels
  const prefillMappings = (headers: string[]) => {
    const preMappings: { [key: string]: string } = {};
    headers.forEach((header) => {
      const lowerHeader = header.toLowerCase().trim();
      // Check if header matches any key in default mapping
      const match = Object.keys(defaultMappingJson).find(
        (key) => key.toLowerCase() === lowerHeader
      );
      if (match) {
        preMappings[header] = defaultMappingJson[match as keyof typeof defaultMappingJson];
      }
    });
    setMappings(preMappings);
  };

  // Removes uploaded file and resets state
  // const removeFile = () => {
  //   setFiles([]);
  //   setPreviewData(null);
  //   setMappings({});
  //   setCustomFieldNames({});
  //   setCurrentStep('upload');
  //   setImportStatus(null);
  //   setRejectedRows([]);
  // };

  // Handles continue button click for import process
  const handleContinue = async () => {
    if (currentStep === 'status') {
      // Close the modal
      resetAndClose();
      return;
    }

    if (currentStep === 'mapping') {
      const isValid = await columnMappingRef.current?.validate();
      if (!isValid) {
        return;
      }
    } else if (currentStep === 'preview' && !allHeadersMatch) {
      setCurrentStep('mapping');
      return;
    }

    // If we're in mapping step or all headers match, proceed with import
    if (currentStep === 'mapping' || allHeadersMatch) {
      await handleImport();
    }
  };

  // Fetch import status
  const fetchImportStatus = async (importId: string) => {
    try {
      const { token, tenantId, userId } = getAuthContext();
      if (!token) {
        toast.error('Authentication required');
        setIsPolling(false);
        return;
      }


      const response = await fetch(`${BASE_URL}/v1/audience-insights/imports/${importId}`, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const statusData = await response.json();
      setImportStatus(statusData);

      // If status is COMPLETED or FAILED, stop polling and show toast
      if (statusData.status === 'COMPLETED') {
        setIsPolling(false);
        toast.success('Contacts imported successfully!');
        onSuccess(); // Refresh the table
      } else if (statusData.status === 'FAILED') {
        setIsPolling(false);
        toast.error(statusData.errorMessage || 'Import failed');
      }
    } catch (error: any) {
      // console.error('❌ Error fetching import status:', error);
      // Don't show error toast for polling failures, just stop polling
      setIsPolling(false);
    }
  };

  // Fetch rejected rows
  const handleFetchRejected = async () => {
    if (!importStatus) return;

    setIsLoadingRejected(true);
    try {
      const { token, tenantId, userId } = getAuthContext();
      if (!token) {
        toast.error('Authentication required');
        setIsLoadingRejected(false);
        return;
      }


      const response = await fetch(`${BASE_URL}/v1/audience-insights/imports/${importStatus.importId}/rejected`, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();

      // Extract only rowNumber and reason from each rejected row
      const formattedRows = data.map((row: any) => ({
        rowNumber: row.rowNumber,
        reason: row.reason
      }));

      setRejectedRows(formattedRows);
      setShowRejectedModal(true);
    } catch (error: any) {
      // console.error('❌ Error fetching rejected rows:', error);
      toast.error(error.message || 'Failed to fetch rejected rows');
    } finally {
      setIsLoadingRejected(false);
    }
  };

  // Reset and close modal
  const resetAndClose = () => {
    setFiles([]);
    setPreviewData(null);
    setMappings({});
    setCustomFieldNames({});
    setCurrentStep('upload');
    setImportMode('create-only');
    setImportStatus(null);
    setRejectedRows([]);
    setIsPolling(false);
    onClose();
  };

  // ==================== IMPORT API CALL ====================
  const handleImport = async () => {
    if (files.length === 0) {
      toast.error('No file to import');
      return;
    }

    setIsImporting(true);
    const file = files[0];

    // Build mapping from the mappings state
    const mapping: { [key: string]: string } = {};
    for (const [fileHeader, target] of Object.entries(mappings)) {
      if (target && target !== 'add_new') {
        mapping[fileHeader] = target;
      }
    }

    try {
      const { token, tenantId, userId } = getAuthContext();
      if (!token) {
        toast.error('Authentication required');
        setIsImporting(false);
        return;
      }


      // Create FormData
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mode', importMode);
      formData.append('dedupeKey', 'EMAIL');
      formData.append('mapping', JSON.stringify(mapping));


      const response = await fetch(`${BASE_URL}/v1/audience-insights/imports`, {
        method: 'POST',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      // Set import ID and start polling
      const importId = result.importId;
      if (importId) {
        // Set initial status
        setImportStatus({
          importId: importId,
          status: 'PENDING',
          processed: 0,
          succeeded: 0,
          rejected: 0,
          progress: 0,
          rejectsUrl: null,
          errorMessage: null
        });
        setIsPolling(true);
        setCurrentStep('status');

        // Immediately fetch status once
        await fetchImportStatus(importId);
      } else {
        throw new Error('No import ID received');
      }

    } catch (error: any) {
      // console.error('❌ Import error:', error);
      toast.error(error.message || 'Failed to import contacts');
      setIsPolling(false);
    } finally {
      setIsImporting(false);
    }
  };

  // Handles back navigation in import steps
  const handleBack = () => {
    if (currentStep === 'mapping') {
      setCurrentStep('preview');
    }
  };

  // Handles sorting of preview table
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === 'asc'
    ) {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Returns sorted rows for preview
  const getSortedRows = () => {
    if (!previewData || !sortConfig) return previewData?.rows || [];
    return [...previewData.rows].sort((a, b) => {
      const valA = (a[sortConfig.key] as any)?.toString().toLowerCase?.() ?? '';
      const valB = (b[sortConfig.key] as any)?.toString().toLowerCase?.() ?? '';
      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Returns sort icon for preview table
  const getSortIcon = (key: string) => {
    if (sortConfig?.key !== key) return null;
    return sortConfig.direction === 'asc' ? (
      <FiChevronUpComponent size={14} className='text-orange-500' />
    ) : (
      <FiChevronDownComponent size={14} className='text-orange-500' />
    );
  };

  // Renders the import status view
  const renderStatusView = () => {
    if (!importStatus) return null;

    const isComplete = importStatus.status === 'COMPLETED';
    const isFailed = importStatus.status === 'FAILED';
    const isProcessing = !isComplete && !isFailed;

    return (
      <div className="space-y-6">
        <h3 className="text-[18px] font-semibold text-[#3F5265]">
          Import Status
        </h3>

        {/* Status Table */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Import ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Processed</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Succeeded</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rejected</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-6 py-4 text-sm text-gray-900">{importStatus.importId}</td>
                <td className="px-6 py-4 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${isComplete ? 'bg-green-100 text-green-800' :
                    isFailed ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                    {importStatus.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{importStatus.processed}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{importStatus.succeeded}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{importStatus.rejected}</td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  <div className="flex items-center">
                    <div className="w-24 bg-gray-200 rounded-full h-2.5 mr-2">
                      <div
                        className="bg-orange-500 h-2.5 rounded-full transition-all duration-300"
                        style={{ width: `${importStatus.progress}%` }}
                      />
                    </div>
                    <span>{importStatus.progress}%</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {isProcessing && (
          <div className="text-sm text-gray-500 flex items-center">
            <svg className="animate-spin h-4 w-4 mr-2 text-orange-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Processing import...
          </div>
        )}

        {/* Get Rejected Rows Link - only show when complete and there are rejected rows */}
        {isComplete && importStatus.rejected > 0 && (
          <div className="mt-4">
            <button
              onClick={handleFetchRejected}
              disabled={isLoadingRejected}
              className="text-orange-500 hover:text-orange-600 font-medium text-sm flex items-center gap-2"
            >
              <FiLinkComponent size={16} />
              {isLoadingRejected ? 'Loading...' : 'Get rejected rows'}
            </button>
          </div>
        )}

        {isComplete && importStatus.rejected === 0 && (
          <div className="mt-4 text-sm text-green-600">
            ✓ All rows imported successfully! No rejected rows.
          </div>
        )}

        {isFailed && importStatus.errorMessage && (
          <div className="mt-4 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
            Error: {importStatus.errorMessage}
          </div>
        )}
      </div>
    );
  };

  // Render Rejected Rows Modal
  const renderRejectedModal = () => {
    if (!showRejectedModal) return null;

    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-[100]">
        <div className="bg-white rounded-2xl shadow-lg w-full max-w-3xl max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b">
            <h3 className="text-[20px] font-semibold text-[#3F5265]">
              Rejected Rows
            </h3>
            <button
              onClick={() => setShowRejectedModal(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <FiXComponent size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {rejectedRows.length === 0 ? (
              <p className="text-gray-500 text-center">No rejected rows found</p>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Row Number</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {rejectedRows.map((row, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 text-sm text-gray-900">{row.rowNumber}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">{row.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end p-6 border-t">
            <button
              onClick={() => setShowRejectedModal(false)}
              className="px-4 py-2 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Renders the import modal with upload, preview, mapping, or status steps
  return (
    <>
      <div className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50'>
        <div
          className={`bg-white rounded-2xl shadow-lg w-full max-w-4xl p-6 ${currentStep === 'upload' && 'h-[70vh]'
            } max-h-[90vh] flex flex-col`}
        >
          {/* Header */}
          <div className='flex justify-between items-center mb-4'>
            <div>
              <h2 className='text-[20px] font-semibold text-[#3F5265]'>
                {currentStep === 'status' ? 'Import Status' :
                  currentStep === 'mapping' && previewData
                    ? 'Map the columns'
                    : 'Import file'}
              </h2>
              <p className='text-[#657584] text-[16px]'>
                {currentStep === 'status' ? 'Track your import progress' :
                  currentStep === 'mapping' && previewData
                    ? 'Map the columns from CSV to the columns in the system'
                    : 'Upload CSV file to import contacts'}
              </p>
            </div>
            <button
              onClick={resetAndClose}
              className='text-gray-500 hover:text-gray-700'
            >
              <FiXComponent size={20} />
            </button>
          </div>

          {/* Main Content */}
          <div className='flex-1 overflow-y-auto'>
            {currentStep === 'upload' && (
              <div
                onClick={() => fileInputRef.current?.click()}
                className='border-2 border-dashed border-gray-200 rounded-lg p-6 text-center cursor-pointer hover:border-orange-400 h-full flex flex-col justify-center items-center'
              >
                <FiUploadCloudComponent
                  className='mx-auto text-gray-400 mb-2'
                  size={32}
                />
                <p className='text-[#3F5265] text-[14px]'>
                  Upload CSV file
                </p>
                <p className='text-[#99A1AF] text-[12px]'>
                  Click or Drag & drop to upload (.csv only)
                </p>
              </div>
            )}

            {currentStep === 'preview' && previewData && (
              <PreviewSection
                previewData={previewData}
                allHeadersMatch={allHeadersMatch}
                handleSort={handleSort}
                getSortIcon={getSortIcon}
                getSortedRows={getSortedRows}
              />
            )}

            {currentStep === 'mapping' && previewData && (
              <ColumnMapping
                ref={columnMappingRef}
                previewData={previewData}
                mappings={mappings}
                setMappings={setMappings}
                customFieldNames={customFieldNames}
                setCustomFieldNames={setCustomFieldNames}
                possibleTargets={possibleTargets}
              />
            )}

            {currentStep === 'status' && renderStatusView()}
          </div>

          {/* Footer */}
          <div className='mt-6 flex justify-end items-center'>
            <div
              className={`flex ${currentStep === 'mapping' && previewData
                ? 'justify-between'
                : 'justify-end'
                } w-full`}
            >
              {currentStep === 'mapping' && previewData && (
                <div className='text-sm text-blue-600 items-center flex'>
                  <i className='mr-2'>!</i> Unmapped column will not be imported
                </div>
              )}

              <div className='flex gap-3'>
                {currentStep === 'status' ? (
                  <button
                    onClick={resetAndClose}
                    className='px-4 py-2 text-sm border rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200'
                  >
                    Close
                  </button>
                ) : (
                  <>
                    {currentStep === 'mapping' && previewData ? null : (
                      <button
                        onClick={handleReupload}
                        className={`px-4 py-2 text-sm border rounded-md ${files.length === 0
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-orange-500 hover:bg-orange-100 border-orange-500'
                          }`}
                        disabled={files.length === 0}
                      >
                        Re-upload
                      </button>
                    )}

                    {/* Import Mode Dropdown */}
                    {(currentStep === 'preview' || currentStep === 'mapping') && files.length > 0 && (
                      <select
                        value={importMode}
                        onChange={(e) => setImportMode(e.target.value as 'create-only' | 'update-only')}
                        className='px-3 py-2 text-sm border rounded-md bg-white text-gray-700 border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500'
                        disabled={isImporting}
                      >
                        <option value="create-only">Create</option>
                        <option value="update-only">Update</option>
                      </select>
                    )}

                    {currentStep === 'mapping' && (
                      <button
                        onClick={handleBack}
                        className='px-4 py-2 text-sm border rounded-md bg-white text-gray-600 hover:bg-gray-100 border-gray-300'
                      >
                        Back
                      </button>
                    )}

                    <button
                      onClick={resetAndClose}
                      className='px-4 py-2 text-sm border rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200'
                    >
                      Cancel
                    </button>
                    <button
                      disabled={files.length === 0 || isImporting}
                      onClick={handleContinue}
                      className={`px-4 py-2 text-sm rounded-md ${files.length === 0 || isImporting
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : allHeadersMatch || currentStep === 'mapping'
                          ? 'bg-green-500 text-white hover:bg-green-600'
                          : 'bg-orange-500 text-white hover:bg-orange-600'
                        }`}
                    >
                      {getButtonText()}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <input
            type='file'
            accept='.csv'
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
        </div>
      </div>

      {/* Rejected Rows Modal */}
      {renderRejectedModal()}
    </>
  );
};

export default ImportAudienceModal;
